import React, { createContext, useState, useEffect, useCallback, useContext, useRef } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const isAuthenticated = !!token && !!user;
  const refreshTimerRef = useRef(null);

  const clearAuth = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    setToken(null);
    setUser(null);
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const scheduleTokenRefresh = useCallback((accessToken) => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }
    try {
      const payload = JSON.parse(atob(accessToken.split('.')[1]));
      const expiresAt = payload.exp * 1000;
      // Refresh 5 minutes before expiry
      const refreshIn = Math.max(expiresAt - Date.now() - 5 * 60 * 1000, 60 * 1000);
      refreshTimerRef.current = setTimeout(async () => {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) {
          clearAuth();
          return;
        }
        try {
          const response = await axios.post(`${API_URL}/api/auth/refresh`, {
            refresh_token: refreshToken,
          });
          const { token: newToken, refresh_token: newRefresh } = response.data;
          localStorage.setItem('token', newToken);
          localStorage.setItem('refresh_token', newRefresh);
          setToken(newToken);
          scheduleTokenRefresh(newToken);
        } catch {
          clearAuth();
        }
      }, refreshIn);
    } catch (err) {
      console.error("Failed to parse token for refresh scheduling:", err);
    }
  }, [clearAuth]);

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      scheduleTokenRefresh(token);
    } catch {
      clearAuth();
    } finally {
      setLoading(false);
    }
  }, [token, scheduleTokenRefresh, clearAuth]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    const { token: newToken, refresh_token: refreshToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    if (refreshToken) {
      localStorage.setItem('refresh_token', refreshToken);
    }
    setToken(newToken);
    setUser(userData);
    scheduleTokenRefresh(newToken);
    return response.data;
  };

  const logout = () => {
    clearAuth();
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  const setAuthFromVerification = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    scheduleTokenRefresh(newToken);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, isAuthenticated, login, logout, updateUser, setAuthFromVerification }}>
      {children}
    </AuthContext.Provider>
  );
};
